import { defineStore } from "pinia";
import { ref } from "vue";
import { debounce } from "@/shared/utils/generalUtils";
import { getNotificationsV2, markNotificationAsRead, deleteNotification as deleteNotificationRequest, deleteAllNotifications as deleteAllNotificationsRequest, markAllNotificationsAsRead as markAllNotificationsAsReadRequest, getUnreadNotificationCount } from '@/domains/notifications/services/notification.service';
import { NOTIFICATION_LIST_FILTER_CATEGORIES, notificationMatchesListCategory } from '@/shared/constants/notificationListCategories';
import { getTranslatedErrorMessage } from '@/shared/utils/errorMessages';
import { NOTIFICATION_TYPES, NOTIFICATION_TYPE_CONFIG, NotificationTypeUtils } from '@/shared/constants/notificationTypes';
import { normalizeNotificationPayload } from '@/domains/live/utils/liveNotificationUtils';

export const notificationStore = defineStore('notificationStore', () => {
  // State
  const notifications = ref([]);
  const unreadCount = ref(0);
  const isLoading = ref(false);
  const hasMore = ref(true);
  const currentSkip = ref(0);
  const pageSize = ref(10);
  const lastFetched = ref(0);
  const lastFetchedCategory = ref(null);
  const availableCategories = ref([...NOTIFICATION_LIST_FILTER_CATEGORIES]);

  // Nouveaux états pour les types de notifications
  const filters = ref({
    type: null,
    category: null,
    priority: null
  });
  const categorizedNotifications = ref({});

  // État pour déclencher l'affichage des toasts de notification
  const newNotification = ref(null);

  /** IDs already applied to the header badge this session (prevents +2 on first WS event) */
  const countedNotificationIds = new Set();
  /** In-flight guard when created + updated race before list insert */
  const processingNotificationIds = new Set();

  let reconcileUnreadTimer = null;

  // Getters
  function getAllNotifications() {
    return notifications.value;
  }

  function getUnreadCount() {
    return unreadCount.value;
  }

  function getIsLoading() {
    return isLoading.value;
  }

  function getHasMore() {
    return hasMore.value;
  }

  function addUnreadCount() {
    unreadCount.value++;
  }

  function cancelPendingUnreadRefresh() {
    if (typeof updateUnreadCount.cancel === 'function') {
      updateUnreadCount.cancel();
    }
    if (reconcileUnreadTimer) {
      clearTimeout(reconcileUnreadTimer);
      reconcileUnreadTimer = null;
    }
  }

  /**
   * Realtime badge bump: at most once per notification id.
   * Cancels pending debounced API refresh that races on first event after reload.
   */
  function incrementUnreadForRealtime(notification) {
    if (!notification?.id || notification.isRead) return;
    if (countedNotificationIds.has(notification.id)) return;

    countedNotificationIds.add(notification.id);
    cancelPendingUnreadRefresh();
    unreadCount.value = Math.max(0, unreadCount.value) + 1;
  }

  async function refreshUnreadCountNow() {
    try {
      const result = await getUnreadNotificationCount();
      unreadCount.value = result.data?.count ?? 0;
    } catch {
      // Fail silently
    }
  }

  function getByType(type) {
    return notifications.value.filter(n => n.type === type);
  }

  function getByCategory(category) {
    return notifications.value.filter(n => {
      const config = NotificationTypeUtils.getConfig(n.type);
      return config.category === category;
    });
  }

  function getUnreadCountByCategory(category) {
    return getByCategory(category).filter(n => !n.isRead).length;
  }

  function getPriorityNotifications(level) {
    return notifications.value.filter(n => {
      const config = NotificationTypeUtils.getConfig(n.type);
      return config.priority === level;
    });
  }

  function getCategorizedNotifications() {
    return categorizedNotifications.value;
  }

  // Actions
  /**
   * Charge les notifications depuis l'API
   * @param {number} skip - Nombre d'éléments à ignorer
   * @param {number} take - Nombre d'éléments à récupérer
   * @param {boolean} includeRead - Inclure les notifications lues
   * @param {boolean} append - Ajouter aux notifications existantes ou remplacer
   * @param {boolean} force - Force refresh even if cached
   */
  async function fetchNotifications(skip = 0, take = pageSize.value, includeRead = true, append = false, force = false) {
    // Basic caching: if asking for first page and we fetched recently, skip unless forced
    const now = Date.now();
    const CACHE_DURATION = 30 * 1000; // 30 seconds
    const activeCategory = filters.value.category || null;
    if (
      !append &&
      skip === 0 &&
      !force &&
      (now - lastFetched.value < CACHE_DURATION) &&
      lastFetchedCategory.value === activeCategory
    ) {
      return;
    }

    if (isLoading.value) return;
    isLoading.value = true;

    try {
      const result = await getNotificationsV2(skip, take, includeRead, activeCategory);

      if (result.success) {
        const newNotifications = (result.data || []).map(normalizeNotificationPayload);
        if (Array.isArray(result.meta?.availableCategories) && result.meta.availableCategories.length) {
          availableCategories.value = result.meta.availableCategories;
        }

        if (append) {
          // Ajouter à la fin pour la pagination
          notifications.value.push(...newNotifications);
        } else {
          // Remplacer complètement
          notifications.value = newNotifications;
          lastFetched.value = Date.now();
          lastFetchedCategory.value = activeCategory;
        }

        // Authoritative baseline from API (not debounced — debounce caused first-event races)
        await refreshUnreadCountNow();

        // Catégoriser les notifications après chargement
        categorizeNotifications();

        // Update pagination state
        // Si on a reçu moins d'éléments que demandés, il n'y a plus de pages
        hasMore.value = newNotifications.length >= take;
        
        // Mettre à jour le skip actuel
        // Toujours mettre à jour currentSkip pour refléter le nombre d'éléments chargés
        // Cela permet à loadMore() de savoir où continuer la pagination
        if (append) {
          // En mode append, on ajoute au skip actuel
          currentSkip.value = skip + newNotifications.length;
        } else {
          // En mode remplacement, on met à jour avec le nombre d'éléments chargés
          // pour que la prochaine page commence après ces éléments
          currentSkip.value = newNotifications.length;
        }
      }
    } catch (error) {
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Upsert notification by id (idempotent for WebSocket replay).
   * Used by WebSocket notification_created; supports event replay after reconnect.
   * @param {Object} notification - Notification payload (must have id)
   */
  function addNotification(notification) {
    notification = normalizeNotificationPayload(notification);
    if (!notification?.id) return;

    const activeCategory = filters.value.category || null;
    if (!notificationMatchesListCategory(notification, activeCategory)) {
      return;
    }

    const existingIndex = notifications.value.findIndex(n => n.id === notification.id);

    if (existingIndex !== -1) {
      notifications.value[existingIndex] = notification;
      categorizeNotifications();
      return;
    }

    if (processingNotificationIds.has(notification.id)) {
      return;
    }
    processingNotificationIds.add(notification.id);
    setTimeout(() => processingNotificationIds.delete(notification.id), 3000);

    notifications.value.unshift(notification);
    incrementUnreadForRealtime(notification);
    categorizeNotifications();
    newNotification.value = notification;
    setTimeout(() => {
      newNotification.value = null;
    }, 100);
  }

  /**
   * Marque une notification comme lue
   * @param {string} id - ID de la notification
   */
  async function markAsRead(id) {
    if (!id) return;

    try {
      const result = await markNotificationAsRead(id);

      if (result.success) {
        // Mettre à jour localement
        const notificationIndex = notifications.value.findIndex(n => n.id === id);

        if (notificationIndex !== -1) {
          const notification = notifications.value[notificationIndex];

          // Si elle n'était pas lue, décrémenter le compteur
          if (!notification.isRead) {
            unreadCount.value = Math.max(0, unreadCount.value - 1);
          }

          // Marquer comme lue
          notification.isRead = true;
          notification.readAt = new Date().toISOString();
        }
      }
    } catch (error) {

    }
  }

  /**
   * Remove a notification from local state only (e.g. WebSocket notification_deleted).
   * @param {string} id
   * @returns {boolean} Whether a notification was removed
   */
  function removeNotificationById(id) {
    if (!id) return false;

    const index = notifications.value.findIndex((n) => n.id === id);
    if (index === -1) return false;

    const [removed] = notifications.value.splice(index, 1);

    if (removed && !removed.isRead) {
      unreadCount.value = Math.max(0, unreadCount.value - 1);
    }

    categorizeNotifications();
    return true;
  }

  /**
   * Supprime une notification spécifique
   * @param {string} id - ID de la notification
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async function deleteNotification(id) {
    if (!id) {
      return { success: false, message: 'Notification ID is required' };
    }

    try {
      const result = await deleteNotificationRequest(id);

      if (result.success) {
        const index = notifications.value.findIndex((n) => n.id === id);

        if (index !== -1) {
          const [removed] = notifications.value.splice(index, 1);

          if (removed && !removed.isRead) {
            unreadCount.value = Math.max(0, unreadCount.value - 1);
          }

          categorizeNotifications();
        }
      }

      return result;
    } catch (error) {
      return { success: false, message: getTranslatedErrorMessage('VERIFICATION_FAILED') };
    }
  }

  /**
   * Supprime toutes les notifications de l'utilisateur
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async function deleteAllNotifications() {
    try {
      const result = await deleteAllNotificationsRequest();

      if (result.success) {
        notifications.value = [];
        unreadCount.value = 0;
        hasMore.value = false;
        currentSkip.value = 0;
        categorizeNotifications();
      }

      return result;
    } catch (error) {
      return { success: false, message: getTranslatedErrorMessage('VERIFICATION_FAILED') };
    }
  }

  /**
   * Met à jour le compteur de notifications non lues
   * Debounced to prevent API spamming
   */
  const updateUnreadCount = debounce(async () => {
    try {
      const result = await getUnreadNotificationCount();
      unreadCount.value = result.data?.count || 0;
    } catch (error) {
      // Fail silently
    }
  }, 2000);

  /**
   * Charge plus de notifications
   */
  async function loadMore() {
    if (!hasMore.value || isLoading.value) return;

    await fetchNotifications(currentSkip.value, pageSize.value, true, true);
  }

  /**
   * Recharge les notifications depuis le début
   */
  async function refresh() {
    currentSkip.value = 0;
    hasMore.value = true;
    await fetchNotifications(0, pageSize.value, true, false, true);
  }

  /**
   * Efface toutes les notifications (utilisé au logout)
   */
  function clearNotifications() {
    notifications.value = [];
    unreadCount.value = 0;
    countedNotificationIds.clear();
    processingNotificationIds.clear();
    cancelPendingUnreadRefresh();
    isLoading.value = false;
    hasMore.value = true;
    currentSkip.value = 0;
    lastFetched.value = 0;
    lastFetchedCategory.value = null;

    // Reset des filtres et catégories
    filters.value = {
      type: null,
      category: null,
      priority: null
    };
    categorizedNotifications.value = {};
  }

  /**
   * Marque toutes les notifications comme lues
   */
  async function markAllAsRead() {
    try {
      const result = await markAllNotificationsAsReadRequest();

      if (result.success) {
        notifications.value = notifications.value.map((notification) => ({
          ...notification,
          isRead: true,
          readAt: notification.readAt || new Date().toISOString()
        }));

        unreadCount.value = 0;
        categorizeNotifications();
      }

      return result;
    } catch (error) {
      return { success: false, message: getTranslatedErrorMessage('VERIFICATION_FAILED') };
    }
  }

  /**
   * Catégorise les notifications par type
   * Groupe toutes les notifications par catégorie en utilisant la configuration des types
   */
  function categorizeNotifications() {
    const categories = {};

    notifications.value.forEach((notification) => {
      const config = NotificationTypeUtils.getConfig(notification.type);
      const category = config.category;

      if (!categories[category]) {
        categories[category] = [];
      }
      categories[category].push(notification);
    });

    categorizedNotifications.value = categories;
  }

  async function setCategoryFilter(category) {
    filters.value.category = category || null;
    currentSkip.value = 0;
    hasMore.value = true;
    await fetchNotifications(0, pageSize.value, true, false, true);
  }

  return {
    // State
    notifications,
    unreadCount,
    isLoading,
    hasMore,
    currentSkip,
    pageSize,
    lastFetched,
    lastFetchedCategory,
    availableCategories,
    filters,
    categorizedNotifications,
    newNotification,

    // Getters
    getAllNotifications,
    getUnreadCount,
    getIsLoading,
    getHasMore,

    // Nouveaux getters pour les types
    getByType,
    getByCategory,
    getUnreadCountByCategory,
    getPriorityNotifications,
    getCategorizedNotifications,

    // Actions
    fetchNotifications,
    addNotification,
    markAsRead,
    updateUnreadCount,
    refreshUnreadCountNow,
    cancelPendingUnreadRefresh,
    incrementUnreadForRealtime,
    loadMore,
    refresh,
    clearNotifications,
    markAllAsRead,
    deleteNotification,
    removeNotificationById,
    deleteAllNotifications,
    categorizeNotifications,
    setCategoryFilter,
  };
});
